import { memo, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useInterval, useNotification } from "../../core";
import { useDebounceFunction } from "../../utils";
import { Button } from "../Button";

import styles from "./style.module.scss";

export type AppVersionCheckerProps = {
  onCheck?: () => Promise<string | null>;
  debounceDelay?: number
  checkVersionInterval?: number
};

const defaultCheckVersion = async () => {
  const response = await fetch(`/?v=${Date.now()}`, {
    mode: "cors",
    method: "HEAD",
  });
  return response.headers.get("last-modified");
};

const reload = () => window.location.reload();

export const AppVersionChecker = memo<AppVersionCheckerProps>((
  {
    onCheck = defaultCheckVersion,
    checkVersionInterval = 10 * 60 * 1000,
    debounceDelay = 2 * 60 * 1000,
  },
) => {
  const location = useLocation();
  const versionRef = useRef("");
  const { push } = useNotification();

  const checkVersion = useCallback(async () => {
    if (!document.hidden) {

      const lastModified = await onCheck();

      if (!versionRef.current) {
        versionRef.current = lastModified ?? "";
      } else if (versionRef.current !== lastModified) {
        push({
          messageKey: "app-update",
          type: "info",
          children: "New version available",
          supportingText: (
            <>
              {`You could `}
              <Button buttonType="linkGray" buttonSize="sm" className={styles.button} children={"reload"} onClick={reload} />
              {` the app to get the newest features`}
            </>
          ),
          autoClose: false,
        });
      }
    }
  }, [ onCheck, push ]);

  const debouncedCheckVersion = useDebounceFunction(checkVersion, debounceDelay);

  useInterval(debouncedCheckVersion, checkVersionInterval);
  useEffect(() => {debouncedCheckVersion();}, [ location, debouncedCheckVersion ]); // on location changed
  useEffect(() => {checkVersion();}, [ checkVersion ]); // startup
  useEffect(() => {
    document.addEventListener("visibilitychange", checkVersion);
    return () => document.removeEventListener("visibilitychange", checkVersion);
  }, [ checkVersion ]); // visibility changed
  return null;
});